<script setup lang="ts">
import { cardTransformer, CardFields } from './cardHelpers'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y } from 'swiper/modules'
import SwiperCore from 'swiper'
import 'swiper/css'

type FourUpCardPanelProps = {
  title?: string
  cards: CardFields[]
}

const props = defineProps<FourUpCardPanelProps>()

const swiper = ref<SwiperCore | null>(null)
const isPrevDisabled = ref(true)
const isNextDisabled = ref(false)

const breakpoints = {
  0: { slidesPerView: 'auto' as const, spaceBetween: 4 },
  768: { slidesPerView: 4, spaceBetween: 4 },
}

const updateButtonState = () => {
  if (swiper.value) {
    isPrevDisabled.value = swiper.value.isBeginning
    isNextDisabled.value = swiper.value.isEnd
  }
}

const onSwiper = (swiperCore: SwiperCore) => {
  swiper.value = swiperCore
  updateButtonState()
}
</script>

<template>
  <section class="fourup-panel" data-testid="fourup-panel">
    <div class="container">
      <div class="fourup-panel__header">
        <h3 class="fourup-panel__title heading-2" v-if="title">{{ title }}</h3>
      </div>

      <Swiper :modules="[A11y]" @swiper="onSwiper" @slideChange="updateButtonState" :breakpoints="breakpoints">
        <SwiperSlide class="fourup-panel__carousel-slide" v-for="(card, index) in cards" :key="index">
          <Card v-bind="cardTransformer(card)" variant="four-up" preset="four-up" />
        </SwiperSlide>

        <div v-show="!isPrevDisabled || !isNextDisabled" class="fourup-panel__carousel-nav">
          <button
            @click="swiper?.slidePrev()"
            :disabled="isPrevDisabled"
            :aria-label="$t('articleSwiper.button.previous')"
          >
            <svg-icon name="mhj:arrow-carousel-left" />
          </button>
          <button @click="swiper?.slideNext()" :disabled="isNextDisabled" :aria-label="$t('articleSwiper.button.next')">
            <svg-icon name="mhj:arrow-carousel-right" />
          </button>
        </div>
      </Swiper>
    </div>
  </section>
</template>

<style lang="postcss">
.fourup-panel {
  margin-bottom: var(--component-space);

  .fourup-panel__header {
    text-align: center;
  }

  .fourup-panel__title {
    margin-bottom: var(--space-70);
  }

  .fourup-panel__carousel-nav {
    margin-top: var(--space-70);
    display: flex;
    align-items: center;
    justify-content: end;
    gap: var(--space-50);

    button {
      height: 3.5rem;
      width: 3.5rem;

      &:disabled {
        fill: var(--ui-dark-10-t40);
        color: var(--ui-dark-10-t40);
        cursor: default;
      }
    }
  }

  .fourup-panel__carousel-slide {
    max-width: 18rem;

    @media (--tablet) {
      max-width: unset;
    }
  }
}
</style>
